export const HARVEST_YIELD_COMPARISON_REPORT_CODE = "PLOT_BASED_HARVESTING_OPERATIONS_YIELD_COMPARISON";

export const harvestYieldComparisonConfig = {
  showSummaryCards: true,
  gridHeightOffset: 265,
  rootClass: "report-grid-view--harvest-yield-comparison",
};

function getParcelKey(row) {
  return row?.parcel_uuid || row?.parcel_name || row?.uuid || null;
}

function getDistinctParcelMap(rows) {
  const map = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const key = getParcelKey(row);
    if (!key) {
      return;
    }

    const current = map.get(key) || {
      parcel_uuid: row.parcel_uuid || key,
      parcel_name: row.parcel_name || "",
      harvest_hectare: 0,
      weighed_quantity: 0,
    };

    current.harvest_hectare = Math.max(current.harvest_hectare, Number(row?.processed_hectare) || 0);
    current.weighed_quantity += Number(row?.quantity) || 0;
    map.set(key, current);
  });

  return map;
}

export function getHarvestYieldComparisonRowStatus(row, { t }) {
  const harvest = Number(row?.processed_hectare) || 0;
  const quantity = Number(row?.quantity) || 0;

  if (harvest > 0 && quantity > 0) {
    return { code: "OK", label: t("reporting.harvest_yield.status.ok"), className: "status-ok" };
  }

  if (harvest > 0 && quantity === 0) {
    return {
      code: "NO_WEIGHING",
      label: t("reporting.harvest_yield.status.harvested_no_weighing"),
      className: "status-no-weighing",
    };
  }

  if (harvest === 0 && quantity > 0) {
    return {
      code: "NO_HARVEST",
      label: t("reporting.harvest_yield.status.weighing_no_harvest"),
      className: "status-no-harvest",
    };
  }

  return {
    code: "MISSING_AREA",
    label: t("reporting.harvest_yield.status.missing_area"),
    className: "status-missing-area",
  };
}

export function buildHarvestYieldComparisonSummaryCards({ rows, t, formatNumber }) {
  const parcelMap = getDistinctParcelMap(rows);
  const parcels = Array.from(parcelMap.values());
  const harvestedPlots = parcels.filter((row) => row.harvest_hectare > 0);
  const weighedPlots = parcels.filter((row) => row.weighed_quantity > 0);
  const missingWeighings = parcels.filter((row) => row.harvest_hectare > 0 && row.weighed_quantity === 0);
  const missingHarvests = parcels.filter((row) => row.harvest_hectare === 0 && row.weighed_quantity > 0);
  const totalHarvestedHectares = parseFloat(parseFloat(parcels.reduce((sum, row) => sum + row.harvest_hectare, 0)).toFixed(1));
  const totalWeighedQuantity = (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + (Number(row?.quantity) || 0), 0);
  const averageKgHa = totalHarvestedHectares > 0 ? totalWeighedQuantity / totalHarvestedHectares : 0;

  return [
    {
      key: "total-plots",
      label: t("reporting.harvest_yield.cards.total_plots"),
      value: formatNumber(parcelMap.size, 0),
      className: "card-neutral",
    },
    {
      key: "harvested-plots",
      label: t("reporting.harvest_yield.cards.harvested_plots"),
      value: formatNumber(harvestedPlots.length, 0),
      className: "card-success",
    },
    {
      key: "weighed-plots",
      label: t("reporting.harvest_yield.cards.weighed_plots"),
      value: formatNumber(weighedPlots.length, 0),
      className: "card-success",
    },
    {
      key: "missing-weighings",
      label: t("reporting.harvest_yield.cards.missing_weighings"),
      value: formatNumber(missingWeighings.length, 0),
      className: "card-warning",
    },
    {
      key: "missing-harvests",
      label: t("reporting.harvest_yield.cards.missing_harvests"),
      value: formatNumber(missingHarvests.length, 0),
      className: "card-danger",
    },
    {
      key: "total-harvested-hectares",
      label: t("reporting.harvest_yield.cards.total_harvested_hectares"),
      value: formatNumber(totalHarvestedHectares, 1),
      className: "card-neutral",
    },
    {
      key: "total-weighed-quantity",
      label: t("reporting.harvest_yield.cards.total_weighed_quantity"),
      value: formatNumber(totalWeighedQuantity, 1),
      className: "card-neutral",
    },
    {
      key: "average-kg-ha",
      label: t("reporting.harvest_yield.cards.average_kg_per_ha"),
      value: formatNumber(averageKgHa, 1),
      className: "card-primary",
    },
  ];
}

export function buildHarvestYieldComparisonRowLegendItems({ t }) {
  return [
    {
      key: "status-ok",
      colorClass: "legend-dot--status-ok",
      label: t("reporting.harvest_yield.status.ok"),
    },
    {
      key: "status-no-weighing",
      colorClass: "legend-dot--status-no-weighing",
      label: t("reporting.harvest_yield.status.harvested_no_weighing"),
    },
    {
      key: "status-no-harvest",
      colorClass: "legend-dot--status-no-harvest",
      label: t("reporting.harvest_yield.status.weighing_no_harvest"),
    },
    {
      key: "status-missing-area",
      colorClass: "legend-dot--status-missing-area",
      label: t("reporting.harvest_yield.status.missing_area"),
    },
  ];
}